import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../services/api.js';
import { navigate } from '../commands/navigate.js';
import { openFeedbackReport } from '../commands/feedbackReport.js';
import { formatDate } from '../utils/dates.js';
import {
	notificationDisplay,
	groupByRecency,
	isNotificationVisible,
	type NotificationRow,
} from '../notifications/display.js';
import './cos-icon.js';
import './cos-notification-preferences.js';

/**
 * <cos-notification-bell> — Stage 38a header bell, enriched in Stage 75.
 * Header-bar bell + badge + dropdown. Eager-loaded (lives in every
 * authenticated page) and remains the single owner of the badge + the
 * live-push hydration.
 *
 * Two data sources feed the bell:
 *
 *  1. On mount: GET /api/v1/notifications/recent backs the dropdown body
 *     (read + unread, ~20, grouped by recency); GET .../unread backs the
 *     badge COUNT.
 *  2. Live: window 'cos:notification' CustomEvent (dispatched by
 *     services/notifications-ws.ts when a server push arrives) prepends a
 *     new row + bumps the badge.
 *
 * Stage 75 additions: recency grouping, per-row Open/Mark-read/Dismiss
 * actions, a gear → preferences modal, and a "View all" footer →
 * /notifications. Display text/link is derived in notifications/display.ts
 * (shared with cos-notifications-page).
 *
 * Click outside / Escape closes the dropdown — pattern mirrored from
 * cos-deal-card's move-menu (Stage 35c).
 */

const DROPDOWN_CAP = 20;

@customElement('cos-notification-bell')
export class CosNotificationBell extends LitElement {
	@state() private _items: NotificationRow[] = [];
	@state() private _unreadCount = 0;
	@state() private _open = false;
	@state() private _loading = false;
	@state() private _prefsOpen = false;

	static styles = css`
		:host {
			position: relative;
			display: inline-flex;
		}
		.bell {
			background: none;
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			width: 40px;
			height: 40px;
			cursor: pointer;
			color: var(--cos-text-primary);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			position: relative;
		}
		.bell:hover { background: var(--cos-surface-raised); }
		.bell:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
		.badge {
			position: absolute;
			top: -4px;
			right: -4px;
			background: var(--cos-status-warning-wash);
			border: 1px solid var(--cos-status-warning-line);
			color: var(--cos-status-warning-text);
			font-size: var(--cos-font-size-2xs);
			font-weight: 700;
			min-width: 18px;
			height: 18px;
			border-radius: 999px;
			padding: 0 5px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: 1;
		}
		.dropdown {
			position: absolute;
			top: 100%;
			right: 0;
			margin-top: 8px;
			width: 360px;
			background: var(--cos-surface);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			box-shadow: var(--cos-shadow-lg);
			z-index: 100;
			max-height: 480px;
			overflow-y: auto;
		}
		.head {
			display: flex;
			align-items: center;
			gap: var(--cos-space-2);
			padding: var(--cos-space-3) var(--cos-space-4);
			border-bottom: 1px solid var(--cos-surface-raised);
			font-weight: 600;
			color: var(--cos-text-primary);
			position: sticky;
			top: 0;
			background: var(--cos-surface);
			z-index: 1;
		}
		.head .title { flex: 1; }
		.head .count {
			color: var(--cos-text-tertiary);
			font-weight: 400;
			font-size: var(--cos-font-size-sm);
		}
		.icon-btn {
			background: none;
			border: none;
			padding: 4px;
			border-radius: var(--cos-radius-sm);
			color: var(--cos-text-secondary);
			cursor: pointer;
			display: inline-flex;
		}
		.icon-btn:hover { background: var(--cos-surface-raised); }
		.icon-btn:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 1px;
		}
		.mark-all {
			background: none;
			border: none;
			padding: 0;
			color: var(--cos-brand-700);
			cursor: pointer;
			font-size: var(--cos-font-size-sm);
			text-decoration: underline;
		}
		.mark-all:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
		.group-label {
			padding: var(--cos-space-2) var(--cos-space-4);
			font-size: var(--cos-font-size-xs);
			font-weight: 600;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			color: var(--cos-text-tertiary);
			background: var(--cos-surface-raised);
		}
		.row {
			display: flex;
			gap: var(--cos-space-3);
			align-items: flex-start;
			padding: var(--cos-space-3) var(--cos-space-4);
			border-bottom: 1px solid var(--cos-surface-raised);
			cursor: pointer;
			color: inherit;
			text-decoration: none;
			position: relative;
		}
		.row:last-child { border-bottom: none; }
		.row:hover, .row:focus-within { background: var(--cos-surface-raised); }
		.dot {
			width: 8px;
			height: 8px;
			border-radius: 999px;
			background: var(--cos-brand-500);
			margin-top: 5px;
			flex: none;
		}
		.dot.placeholder { background: transparent; }
		.glyph {
			color: var(--cos-text-tertiary);
			flex: none;
			margin-top: 1px;
		}
		.body { flex: 1; min-width: 0; }
		.msg {
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-primary);
			margin-bottom: 2px;
		}
		.sub {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-secondary);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.ts {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-tertiary);
		}
		.row-actions {
			display: none;
			gap: 2px;
			flex: none;
		}
		.row:hover .row-actions, .row:focus-within .row-actions { display: inline-flex; }
		.empty {
			padding: var(--cos-space-6);
			text-align: center;
			color: var(--cos-text-tertiary);
			font-size: var(--cos-font-size-sm);
		}
		.foot {
			padding: var(--cos-space-3) var(--cos-space-4);
			text-align: center;
			border-top: 1px solid var(--cos-surface-raised);
			position: sticky;
			bottom: 0;
			background: var(--cos-surface);
		}
		.view-all {
			background: none;
			border: none;
			padding: 0;
			color: var(--cos-brand-700);
			cursor: pointer;
			font-size: var(--cos-font-size-sm);
			text-decoration: underline;
		}
		.view-all:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
	`;

	connectedCallback(): void {
		super.connectedCallback();
		this._hydrate();
		window.addEventListener('cos:notification', this._onPush as EventListener);
		document.addEventListener('click', this._onDocClick, true);
		document.addEventListener('keydown', this._onDocKey);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener('cos:notification', this._onPush as EventListener);
		document.removeEventListener('click', this._onDocClick, true);
		document.removeEventListener('keydown', this._onDocKey);
	}

	private async _hydrate() {
		this._loading = true;
		try {
			// Dropdown body = recent (read + unread). Badge = unread count.
			const [recent, unread] = await Promise.all([
				api.listRecentNotifications(DROPDOWN_CAP) as Promise<{ data: NotificationRow[]; total: number }>,
				api.listUnreadNotifications(DROPDOWN_CAP) as Promise<{ data: NotificationRow[]; total: number }>,
			]);
			this._items = recent.data || [];
			// A row this login is not to see is not counted either.
			const hidden = (unread.data || []).filter((r) => !isNotificationVisible(r)).length;
			this._unreadCount = Math.max(0, (unread.total ?? (unread.data || []).length) - hidden);
		} catch {
			// Swallow — bell stays at 0 + dropdown empty until next push.
		} finally {
			this._loading = false;
		}
	}

	private _onPush = (e: CustomEvent) => {
		const n = e.detail as NotificationRow;
		if (!isNotificationVisible(n)) return;
		this._items = [n, ...this._items].slice(0, DROPDOWN_CAP);
		if (!n.read_at) this._unreadCount += 1;
	};

	private _onDocClick = (e: MouseEvent) => {
		if (!this._open) return;
		const path = e.composedPath();
		if (path.includes(this)) return;
		this._open = false;
	};

	private _onDocKey = (e: KeyboardEvent) => {
		if (this._open && e.key === 'Escape' && !this._prefsOpen) {
			e.preventDefault();
			this._open = false;
		}
	};

	private _toggle = (e: Event) => {
		e.stopPropagation();
		this._open = !this._open;
	};

	private async _markAllRead() {
		try {
			await api.markAllNotificationsRead();
			this._items = this._items.map((it) => ({ ...it, read_at: new Date().toISOString() }));
			this._unreadCount = 0;
		} catch {
			// Silent — UI will re-hydrate on next push.
		}
	}

	private async _markRead(n: NotificationRow, e?: Event) {
		e?.preventDefault();
		e?.stopPropagation();
		if (n.read_at) return;
		try {
			await api.markNotificationRead(n.id);
		} catch {
			/* optimistic anyway */
		}
		this._items = this._items.map((it) =>
			it.id === n.id ? { ...it, read_at: new Date().toISOString() } : it,
		);
		this._unreadCount = Math.max(0, this._unreadCount - 1);
	}

	private async _dismiss(n: NotificationRow, e?: Event) {
		e?.preventDefault();
		e?.stopPropagation();
		try {
			await api.dismissNotifications([n.id]);
		} catch {
			/* optimistic anyway */
		}
		const wasUnread = !n.read_at;
		this._items = this._items.filter((it) => it.id !== n.id);
		if (wasUnread) this._unreadCount = Math.max(0, this._unreadCount - 1);
	}

	private _open_(n: NotificationRow, e?: Event) {
		e?.preventDefault();
		const d = notificationDisplay(n);
		if (!n.read_at) void this._markRead(n);
		this._open = false;
		if (d.feedbackReportId) {
			openFeedbackReport(d.feedbackReportId);
			return;
		}
		if (d.href && d.href !== '#') navigate(d.href);
	}

	private _viewAll = () => {
		this._open = false;
		navigate('/notifications');
	};

	private _openPrefs = (e: Event) => {
		e.stopPropagation();
		this._prefsOpen = true;
	};

	private _renderRow(n: NotificationRow) {
		const d = notificationDisplay(n);
		return html`
			<a
				class="row"
				href=${d.href}
				role="menuitem"
				@click=${(e: Event) => this._open_(n, e)}
			>
				<span class=${`dot ${n.read_at ? 'placeholder' : ''}`} aria-hidden="true"></span>
				<cos-icon class="glyph" name=${d.icon} size="16"></cos-icon>
				<span class="body">
					<span class="msg">${d.label}</span>
					${d.subtitle ? html`<span class="sub">${d.subtitle}</span>` : ''}
					<span class="ts">${formatDate(n.created_at, 'relative')}</span>
				</span>
				<span class="row-actions">
					${n.read_at
						? ''
						: html`<button
								class="icon-btn"
								type="button"
								title="Mark as read"
								aria-label="Mark as read"
								@click=${(e: Event) => this._markRead(n, e)}
							>
								<cos-icon name="check" size="14"></cos-icon>
							</button>`}
					<button
						class="icon-btn"
						type="button"
						title="Dismiss"
						aria-label="Dismiss"
						@click=${(e: Event) => this._dismiss(n, e)}
					>
						<cos-icon name="x" size="14"></cos-icon>
					</button>
				</span>
			</a>
		`;
	}

	render() {
		const badge = this._unreadCount > 99 ? '99+' : String(this._unreadCount);
		const shown = this._items.filter(isNotificationVisible);
		const groups = groupByRecency(shown);
		return html`
			<button class="bell" type="button"
				aria-label=${`Notifications (${this._unreadCount} unread)`}
				aria-expanded=${this._open ? 'true' : 'false'}
				aria-haspopup="menu"
				@click=${this._toggle}>
				<cos-icon name="bell" size="20"></cos-icon>
				${this._unreadCount > 0 ? html`<span class="badge">${badge}</span>` : ''}
			</button>
			${this._open ? html`
				<div class="dropdown" role="menu">
					<div class="head">
						<span class="title">Notifications ${this._unreadCount > 0 ? html`<span class="count">${this._unreadCount} unread</span>` : ''}</span>
						${this._unreadCount > 0
							? html`<button class="mark-all" type="button" @click=${this._markAllRead}>Mark all as read</button>`
							: ''}
						<button class="icon-btn" type="button" title="Notification settings" aria-label="Notification settings" @click=${this._openPrefs}>
							<cos-icon name="pencil" size="16"></cos-icon>
						</button>
					</div>
					${shown.length === 0
						? html`<div class="empty">${this._loading ? 'Loading…' : 'No notifications yet'}</div>`
						: groups.map(
								(g) => html`
									<div class="group-label">${g.group}</div>
									${g.rows.map((n) => this._renderRow(n))}
								`,
							)}
					<div class="foot">
						<button class="view-all" type="button" @click=${this._viewAll}>View all</button>
					</div>
				</div>
			` : ''}
			${this._prefsOpen
				? html`<cos-notification-preferences @close=${() => (this._prefsOpen = false)}></cos-notification-preferences>`
				: ''}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-notification-bell': CosNotificationBell;
	}
}
